import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, TrendingUp, Sparkles } from "lucide-react";
import { api } from "../api";
import { useUser } from "../context/UserContext";
import { fmtVND, fmtUSD, fmtDate } from "../lib/format";
import StatusChip from "../components/StatusChip";

export default function WalletHome() {
  const { activeUserId, activeUser } = useUser();
  const nav = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeUserId) return;
    setLoading(true);
    Promise.all([api.wallet(activeUserId), api.ledger(activeUserId)])
      .then(([w, l]) => {
        setWallet(w);
        setEntries(l.slice(0, 8));
      })
      .finally(() => setLoading(false));
  }, [activeUserId]);

  if (!activeUser) return null;

  const totalEarnings = entries
    .filter((e) => e.type === "earning" && e.status === "completed")
    .reduce((s, e) => s + e.amount_vnd, 0);

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-1">
        <p className="text-sm text-slate-500">Welcome back,</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">
          {activeUser.name.split(" (")[0]}
        </h1>
      </header>

      {/* Balance card */}
      <div
        data-testid="wallet-balance-card"
        className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm"
      >
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-teal-700/5" />
        <div className="pointer-events-none absolute -left-16 -bottom-24 h-64 w-64 rounded-full bg-orange-500/5" />

        <div className="relative flex flex-col gap-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">
              Available balance
            </p>
            <div className="mt-2 flex items-baseline gap-3 flex-wrap">
              <div
                data-testid="balance-vnd"
                className="balance-hero text-5xl sm:text-6xl md:text-7xl text-slate-900 tabular-nums"
              >
                {loading ? "…" : fmtVND(wallet?.balance_vnd)}
              </div>
              <div className="text-sm text-slate-500 font-medium">VND</div>
            </div>
            <div
              data-testid="balance-usd"
              className="mt-2 text-lg sm:text-xl text-slate-500 font-medium tabular-nums"
            >
              ≈ {loading ? "…" : fmtUSD(wallet?.balance_usd_equivalent)} USD
              <span className="ml-2 text-xs text-slate-400">
                @ 1 USD = 26,000 VND
              </span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              data-testid="withdraw-cta"
              onClick={() => nav("/withdraw")}
              className="group flex items-center justify-center gap-2 rounded-full bg-orange-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-orange-500/25 transition-transform hover:-translate-y-0.5 hover:bg-orange-600 active:translate-y-0"
            >
              Withdraw funds
              <ArrowUpRight
                className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={2.25}
              />
            </button>
            <button
              onClick={() => nav("/activity")}
              data-testid="view-activity-cta"
              className="rounded-full border border-slate-200 bg-white px-6 py-4 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              View activity
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                <TrendingUp className="h-3 w-3" /> Last 8 entries earnings
              </p>
              <p className="mt-1 font-display text-2xl font-bold text-slate-900 tabular-nums">
                {fmtVND(totalEarnings)}
              </p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" /> Rail
              </p>
              <p className="mt-1 font-display text-2xl font-bold text-slate-900">
                Pay10x · Thunes
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <section>
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="font-display text-xl font-semibold text-slate-900">
            Recent activity
          </h2>
          <button
            onClick={() => nav("/activity")}
            className="text-sm font-medium text-teal-700 hover:text-teal-800"
            data-testid="see-all-activity"
          >
            See all →
          </button>
        </div>
        <div
          data-testid="activity-feed"
          className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100"
        >
          {loading && (
            <div className="p-6 text-sm text-slate-500">Loading…</div>
          )}
          {!loading && entries.length === 0 && (
            <div className="p-6 text-sm text-slate-500">No activity yet.</div>
          )}
          {entries.map((e) => (
            <div
              key={e.id}
              className="flex items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50 transition-colors"
              data-testid={`activity-row-${e.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`h-2.5 w-2.5 rounded-full shrink-0 ${
                      e.direction === "credit" ? "bg-emerald-500" : "bg-slate-400"
                    }`}
                  />
                  <p className="truncate font-medium text-slate-900 text-sm">
                    {e.description}
                  </p>
                </div>
                <div className="mt-1 ml-4.5 flex items-center gap-2 text-xs text-slate-500">
                  <span>{fmtDate(e.created_at)}</span>
                  <span>•</span>
                  <StatusChip status={e.status} />
                </div>
              </div>
              <div
                className={`shrink-0 font-display text-base sm:text-lg font-semibold tabular-nums ${
                  e.direction === "credit" ? "text-emerald-700" : "text-slate-900"
                }`}
              >
                {e.direction === "credit" ? "+" : "−"} {fmtVND(e.amount_vnd)}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
